
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { DeletedLinksTypeEnum } from '@api/api';
import { fetchPaths } from '@queries/usePaths';
import * as Sentry from '@sentry/nextjs';
import { handleDeletedLink } from '@utils/handleDeletedLink';
import { buildArticleLink, buildSubcategoryPageLink } from '@utils/link';
import { isArticle, shouldHandleArticlePage } from '@utils/page';
import {
  getArticlePageProps,
  getPreviewArticlePageProps,
  getSubcategoryPageProps,
} from '@utils/pageFetchers';
import { getArticleSlugs, getSubcategorySlugs } from '@utils/slugs';
import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import { QueryClient } from 'react-query';

const ArticlePage = dynamic(
  () => import('@components/layout/PageContent/ArticlePage').then((mod) => mod.ArticlePage),
  { ssr: true },
);
const SubcategoryPage = dynamic(
  () => import('@components/layout/PageContent/SubcategoryPage').then((mod) => mod.SubcategoryPage),
  { ssr: true },
);

export default function BlogPage({
  type,
  seo,
  id,
  categorySlug,
  articleSlug,
  slug,
  preview,
  ads,
  affiliateLinks,
  affiliateLinksHeading,
  topReads,
  latestNews,
}) {
  if (isArticle(type)) {
    return (
      <ArticlePage
        id={id}
        seo={seo}
        articleSlug={articleSlug}
        isPreview={preview}
        ads={ads}
        affiliateLinks={affiliateLinks}
        affiliateLinksHeading={affiliateLinksHeading}
        topReads={topReads}
        latestNews={latestNews}
      />
    );
  }
  return <SubcategoryPage seo={seo} categorySlug={categorySlug} slug={slug} />;
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const { article, subcategory } = await fetchPaths();

  if (!article || !subcategory) {
    return {
      fallback: 'blocking',
      paths: [],
    };
  }

  const articleSlugs = getArticleSlugs(article, locales);
  const subcategorySlugs = getSubcategorySlugs(subcategory, locales);

  return {
    paths: [...articleSlugs, ...subcategorySlugs],
    fallback: 'blocking',
  };
};

 const _getStaticProps: GetStaticProps = async (context) => {
  const { slug, slugs = [] } = context.params;
  const queryClient = new QueryClient();
  const handleArticlePage = shouldHandleArticlePage(slug);

  if (handleArticlePage) {
    const { preview = null, previewData = { previewSource: null, id: undefined } } = context;
    // Can't fix it right now, next has to do it on their side
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { previewSource, id: previewId } = previewData;
    const isDraftArticle = preview ? String(previewId) === String(slug) : false;
    const articleId = String(slug);
    const articleSlug = String(slugs);

    if (isDraftArticle) {
      try {
        const props = await getPreviewArticlePageProps({
          articleId,
          articleSlug,
          queryClient,
          previewSource,
        });

        return {
          props,
          revalidate: 10,
        };
      } catch (e) {
        return {
          notFound: true,
          revalidate: 10,
        };
      }
    }

    try {
      const props = await getArticlePageProps({ articleId, articleSlug, queryClient });

      return {
        props,
        revalidate: 600,
      };
    } catch {
      const shouldRedirectToArticlePage = shouldHandleArticlePage(slug);

      if (shouldRedirectToArticlePage) {
        try {
          return handleDeletedLink(
            DeletedLinksTypeEnum.Article,
            String(slugs),
            (item) => buildArticleLink(String(item.itemId), item.redirectTo),
            10,
          );
        } catch (e) {
          Sentry.captureException(e);
          return {
            notFound: true,
            revalidate: 10,
          };
        }
      }
    }
  }

  try {
    const categorySlug = String(slug);
    const subcategorySlug = String(slugs);

    const props = await getSubcategoryPageProps({ subcategorySlug, categorySlug, queryClient });

    return {
      props,
      revalidate: 600,
    };
  } catch (error) {
    try {
      return handleDeletedLink(DeletedLinksTypeEnum.Subcategory, String(slugs), (item) =>
        buildSubcategoryPageLink(String(slug), item.redirectTo),
      );
    } catch {
      return {
        notFound: true,
        revalidate: 10,
      };
    }
  }
};


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/[slug]/[...slugs]',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  